import React, { useEffect, useLayoutEffect, useRef, useState, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { fetchStructuredProfile } from '../../services/api';
import { hasRealProfileData } from '../../utils/profileMeta';
import ConciergeChat from './ConciergeChat';
import AstraAvatar from './AstraAvatar';
import { track, EVENTS } from '../../services/analytics';
import useAssentStatus from '../../hooks/useAssentStatus';
import { OPEN_ASTRA_EVENT, ASTRA_PATH, TODAY_PATH, astraHref } from '../../utils/astraLink';
import { useAstraToday, openMoves, topMoveId, thingsLine } from '../../services/astraToday';
import { runCarryImport } from '../../services/astraCarry';
import { isOnboardingPath } from '../../utils/onboardingLanding';
import { useOnboardingHold } from '../../services/onboardingHold';

const DISMISS_KEY_PREFIX = 'conciergeDismissed_';

/**
 * The Concierge's presence in the app (CEO-approved 2026-08-02). A floating
 * launcher, shown to every signed-in user — PERSISTENT, not onboarding-only (founder
 * 2026-08-02, after a refresh hid it mid-journey: "where do I find it?").
 * The concierge carries server-side memory and a counseling persona; its
 * relationship with the student doesn't end when the first profile field
 * saves, so neither does its button. Dismissible per session
 * (sessionStorage, same pattern as LowCreditsBanner).
 *
 * On lg+ the floating pill is retired — the nav's Astra dock (N3,
 * 2026-08-09) is the desktop door, opening this same mounted panel via the
 * 'stratia:open-astra' window event; the pill covered card CTAs on every
 * long page. On mobile (no sidebar) the pill remains the entry.
 *
 * AUTO-OPENS itself only for a brand-new student (profile has no real data
 * yet, per hasRealProfileData) — the one moment the concierge is the
 * primary path to value. Everyone else gets the quiet pill and opens it
 * when they want it; reopening resumes the same conversation server-side.
 */
export default function ConciergeLauncher() {
    const [open, setOpen] = useState(false);
    const [needsProfile, setNeedsProfile] = useState(false);
    const [dismissed, setDismissed] = useState(false);
    // A question handed in by another surface, waiting for the chat to mount.
    const [pendingAsk, setPendingAsk] = useState(null);
    // The university that ask is ABOUT (#1617) — the conversation gets filed
    // under it. Null for every surface that isn't about one school.
    const [pendingScope, setPendingScope] = useState(null);
    // A specific existing thread to open (the Research drawer's "Resume").
    const [pendingThread, setPendingThread] = useState(null);
    const [checked, setChecked] = useState(false);
    // Gates the FIRST mount only — before any open, ConciergeChat isn't in
    // the DOM at all (no wasted kickoff call). Once opened, it stays true
    // for the component's lifetime so later closes only hide, never unmount.
    const [hasOpenedOnce, setHasOpenedOnce] = useState(false);

    // Identity via AuthContext, like every other surface — not a direct
    // firebaseAuth.currentUser read, which (a) never updates reactively and
    // (b) bypasses the E2E test identity, which is why the panel was
    // untestable in CI until 2026-08-21.
    const { currentUser } = useAuth();
    const email = currentUser?.email;
    const assented = useAssentStatus(email) === 'assented';
    const navigate = useNavigate();
    const launcherBtnRef = useRef(null);
    // Two triggers (mount/email-change, and onProfileMaybeChanged after a
    // save) can have requests in flight at once; without a generation guard
    // a slower, stale response can resolve after a newer one and silently
    // overwrite needsProfile with wrong state.
    const requestGenRef = useRef(0);
    // The launcher button unmounts while the panel is open (it's the `!open`
    // branch below), so its ref is null at the moment we ask to close —
    // focus must be applied AFTER the re-render puts the button back in the
    // DOM, not synchronously inside the click handler.
    const refocusPendingRef = useRef(false);

    const checkProfile = useCallback(async () => {
        if (!email) { setChecked(true); return; }
        const gen = ++requestGenRef.current;
        try {
            const res = await fetchStructuredProfile(email);
            if (gen !== requestGenRef.current) return;
            setNeedsProfile(!hasRealProfileData(res?.profile));
        } catch {
            if (gen !== requestGenRef.current) return;
            // Fail closed on the launcher's SIDE — an unreadable profile
            // should not surface an onboarding nudge for a possibly-active
            // user; if it turns out they do need it, the next poll catches it.
            setNeedsProfile(false);
        } finally {
            if (gen === requestGenRef.current) setChecked(true);
        }
    }, [email]);

    useEffect(() => {
        if (!email) return;
        try { setDismissed(sessionStorage.getItem(DISMISS_KEY_PREFIX + email) === '1'); }
        catch { /* ignore */ }
        checkProfile();
    }, [email, checkProfile]);

    // A landing conversation still in this tab is saved to the account the
    // moment Astra is open to this student (#2945): signed in, 13+
    // attested. Wherever sign-in lands, not only on /astra; the drawer and
    // /astra then show the notice. One run per page life (astraCarry).
    useEffect(() => {
        if (email && assented) runCarryImport(email);
    }, [email, assented]);

    // Auto-open, once, ONLY for a brand-new student (empty profile) — for
    // everyone else the pill waits quietly. useLayoutEffect (not useEffect)
    // so this resolves before the browser paints — otherwise the
    // closed-panel launcher button can flash for a frame right before the
    // panel opens over it.
    //
    // NEVER over the /welcome grade picker (#1897, closing #1844's named
    // root cause): on mobile the panel is full-viewport and was covering
    // the grade cards before first paint — 4 of 8 tracked first-screen
    // exits show exactly that hijack. The pill itself stays: choosing
    // Astra remains one tap; only the ambush goes. pathname is a dep, so
    // leaving /welcome re-evaluates and the greet can still happen on the
    // next page (unless dismissed).
    const { pathname } = useLocation();
    // Nothing over the first read's wait on /start (#5085); the pill comes
    // back the moment the read lands.
    const heldForFirstRead = useOnboardingHold();
    const astraToday = useAstraToday();
    const astraThings = astraToday.enabled ? openMoves(astraToday.moves).length : 0;
    const staleAfterWelcomeRef = useRef(false);
    useLayoutEffect(() => {
        if (isOnboardingPath(pathname)) {
            // Whatever we knew about the profile predates the wizard the
            // student is inside right now — it must be re-read on exit.
            staleAfterWelcomeRef.current = true;
            return;
        }
        if (staleAfterWelcomeRef.current) {
            // Reviewer catch (2026-09-01): a student who COMPLETED the
            // grade picker leaves /welcome with a real profile, but
            // needsProfile was read at mount when it was empty — the
            // deferred greet would ambush the /launchpad mission handoff,
            // the same defect class one screen later. Re-read first; the
            // checked flip guarantees this effect re-runs on the answer,
            // so the skip path (still-empty profile) still greets.
            staleAfterWelcomeRef.current = false;
            setChecked(false);
            checkProfile();
            return;
        }
        // Never on Astra's own page either: the page already holds the
        // conversation, and a drawer on top would run a second kickoff
        // (review, 2026-09-15).
        if (pathname !== ASTRA_PATH && email && assented && checked && needsProfile && !dismissed && !hasOpenedOnce) {
            setHasOpenedOnce(true);
            track(EVENTS.CONCIERGE_OPENED, { trigger: 'auto' });
            setOpen(true);
        }
    }, [pathname, email, assented, checked, needsProfile, dismissed, hasOpenedOnce, checkProfile]);

    // WAI-ARIA APG dismissible-widget pattern: return focus to the control
    // that opened it, rather than dropping a keyboard/screen-reader user to
    // document.body. Runs post-commit, once the launcher button has
    // actually remounted.
    useEffect(() => {
        if (!open && refocusPendingRef.current) {
            refocusPendingRef.current = false;
            launcherBtnRef.current?.focus();
        }
    }, [open]);

    const closeAndRestoreFocus = () => {
        refocusPendingRef.current = true;
        setOpen(false);
    };

    const dismiss = () => {
        try { sessionStorage.setItem(DISMISS_KEY_PREFIX + email, '1'); } catch { /* ignore */ }
        setDismissed(true);
        closeAndRestoreFocus();
    };

    // Every door into Astra (#2210) lands on her PAGE. The nav dock, the
    // fourteen AskAstraButtons, the Major Map prompts and the card's Research
    // drawer all still dispatch the one event they always have; this listener
    // is what changed — it carries the request into the URL instead of
    // sliding a drawer over whatever the student was reading.
    //
    // The drawer is not gone: it still owns the one case that must NOT
    // navigate, the uninvited greeting for a brand-new student (auto-open
    // above). Hijacking a page they chose is exactly what #1897 was about.
    useEffect(() => {
        // The event optionally carries a QUESTION to open with (Major Map's
        // "Talk it through with Astra", 2026-08-09), a SCOPE (the university
        // the ask is about, #1617), or a THREAD to resume (the card's Research
        // drawer). One event, one door — openers that pass nothing land on a
        // plain /astra.
        const openFromNav = (e) => {
            const q = typeof e?.detail?.question === 'string' ? e.detail.question.trim() : '';
            const scope = e?.detail?.scope?.university_id ? e.detail.scope : null;
            const threadId = typeof e?.detail?.threadId === 'string' ? e.detail.threadId : null;
            const topic = typeof e?.detail?.topic === 'string' ? e.detail.topic : null;
            const move = typeof e?.detail?.move === 'string' ? e.detail.move : null;
            track(EVENTS.CONCIERGE_OPENED, {
                trigger: threadId ? 'resume' : (q ? 'question' : (move ? 'move' : 'nav')),
                ...(scope ? { scoped: true } : {}),
                surface: 'page',
            });
            // The drawer may be open over the page (the new-student greeting):
            // close it on the way, or the student arrives at /astra reading it
            // through a panel.
            setOpen(false);
            navigate(astraHref({ question: q, scope, threadId, topic, move }));
        };
        window.addEventListener(OPEN_ASTRA_EVENT, openFromNav);
        return () => window.removeEventListener(OPEN_ASTRA_EVENT, openFromNav);
    }, [navigate]);

    // The chat itself calls this after a successful turn that saved fields —
    // re-check so the launcher retires itself for a FUTURE visit the instant
    // the profile is real, without waiting for a full page reload.
    const onProfileMaybeChanged = useCallback(() => { checkProfile(); }, [checkProfile]);

    // Render gate (2026-08-03, fourth revision): signed in. That's
    // ALL — nothing about the student's state decides whether they can REACH
    // the concierge.
    //
    // Two gates have now been removed for causing the same bug. The profile's
    // state used to hide it after a refresh mid-journey, because a half-built
    // profile counted as "job done" (founder: "where do I find it?"). Then
    // `dismissed` did it again by another route: "Not right now" unmounted the
    // pill for the whole tab session, and since the flag lives in
    // sessionStorage a refresh could not bring it back — only a new tab
    // (#1193, founder: "The concierge button disappeared when I clicked not
    // right now"). Dismissing means "stop opening YOURSELF", which is the
    // auto-open effect above; it has never meant "take the door away".
    if (!email) return null;
    // Nothing Astra does may precede the 13+/Terms attestation (#1750). She is
    // mounted globally, a sibling of AppLayout rather than a child, so the gate
    // modal rendering on top of her was never enough — she had already greeted
    // the student and opened an input behind it. 'unknown' hides too: a flash
    // of Astra before the gate resolves is the same defect, briefly.
    if (!assented) return null;
    return (
        <>
            {/* Kept mounted whenever it's eligible to show, visibility only
                toggled — closing must never discard messages/history/kickoff
                state, and reopening must never re-fire a fresh (rate-limited)
                kickoff turn. See ConciergeChat's `visible` prop. */}
            {/* Nothing to launch while the student is standing on her page. */}
            {/* Also hidden on Astra Today (A1): the page IS her next step. */}
            {!open && pathname !== ASTRA_PATH && pathname !== TODAY_PATH && !heldForFirstRead && (
                <button
                    ref={launcherBtnRef}
                    /* The mobile pill is a door like every other one now: it
                       goes to her page (#2210) rather than sliding the drawer
                       over the screen the student is on. */
                    onClick={() => {
                        // The pill's words promise a step: open Astra on it (#3786).
                        const move = topMoveId(astraToday);
                        track(EVENTS.CONCIERGE_OPENED, { trigger: move ? 'launcher_move' : 'launcher', surface: 'page' });
                        setOpen(false);
                        navigate(move ? astraHref({ move }) : ASTRA_PATH);
                    }}
                    data-testid="concierge-launcher"
                    aria-label={astraThings
                        ? `Open Astra, ${thingsLine(astraThings)}`
                        : 'Open Astra'}
                    /* Lifted clear of the docked first-read bar (#2359):
                       the bar publishes its measured height, 0 when absent. */
                    style={{ bottom: 'calc(1.5rem + var(--first-read-bar-h, 0px))' }}
                    className="fixed right-6 z-40 flex items-center gap-2.5 rounded-full
                        bg-[#1A4D2E] px-5 py-3 text-white shadow-xl shadow-[#1A4D2E]/20
                        hover:bg-[#2D6B45] transition-all hover:scale-[1.03] lg:hidden"
                >
                    {/* Astra (founder pick 2026-08-06) — per aspera ad
                        astra. The avatar replaces both the lifebuoy and the
                        ping dot: the mark itself is gently alive. */}
                    <AstraAvatar size={22} />
                    <span className="flex flex-col items-start leading-tight">
                        <span className="text-sm font-semibold">Astra</span>
                        {/* The pill is where a student meets Astra before
                            they ever open it, so it has to answer "what is
                            this?" in one breath. "Counselor" is the word the
                            persona already uses; "anytime" is the part a
                            school counselor with 400 students can't offer. */}
                        {/* B2 (#3754): the same words as the sidebar row
                            when Astra Today holds a move for the student. */}
                        {astraThings ? (
                            <span className="flex items-center gap-1.5 text-[11px] font-semibold text-[#FFE2A6]"
                                  data-testid="launcher-astra-things">
                                <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-[#F5B942]" aria-hidden="true" />
                                {thingsLine(astraThings)}
                            </span>
                        ) : (
                            <span className="text-[11px] font-normal text-[#C7E3CB]">
                                Questions, any time
                            </span>
                        )}
                    </span>
                    {/* The BETA badge (2026-08-04 → 2026-08-06) is retired: founder call
                        at release to the first paying cohort — "Beta" undercuts
                        the trust that cohort needs. */}
                </button>
            )}
            {hasOpenedOnce && (
                <ConciergeChat
                    visible={open}
                    onClose={closeAndRestoreFocus}
                    onDismissForSession={dismiss}
                    onProfileMaybeChanged={onProfileMaybeChanged}
                    email={email}
                    initialAsk={pendingAsk}
                    initialScope={pendingScope}
                    initialThreadId={pendingThread}
                    onAskConsumed={() => { setPendingAsk(null); setPendingScope(null); }}
                    onThreadConsumed={() => setPendingThread(null)}
                />
            )}
        </>
    );
}
